// Help: how each screen works, one page with tabs (Selling · Buying ·
// Stock · Money · Companies · Accounting · GST). Each tab lists its topics as sections that open and
// close; the search box looks through every tab. A staff login only sees
// topics for pages it can open. The words live in utils/helpContent.ts.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { HeaderSearch } from '../components/HeaderControls';
import { PageTabs } from '../components/ui/PageTabs';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { isIndian } from '../utils/tradingSystem';
import { canAccessPage } from '../utils/pages';
import { HELP_TABS, HELP_TOPICS, type HelpTabKey, type HelpTopic } from '../utils/helpContent';

const TAB_KEY = 'help-tab';

function IconChevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconEmptySearch() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.6" />
      <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/** Every word of a topic, for the search box. */
function topicText(t: HelpTopic): string {
  return [t.title, t.intro, ...t.sections.flatMap(s => [s.title, ...(s.steps ?? []), ...(s.points ?? [])])]
    .join(' ').toLowerCase();
}

export default function Help() {
  const { user } = useAuth();
  const indian = isIndian(useSettings());
  // Only topics for pages this login can open; GST only for Indian companies.
  const topics = useMemo(
    () => HELP_TOPICS.filter(t => canAccessPage(user, t.page) && (!t.indianOnly || indian)),
    [user, indian]);
  const tabs = HELP_TABS.filter(tab => topics.some(t => t.tab === tab.key));

  const [tab, setTabState] = useState<HelpTabKey>(() => {
    try {
      const saved = localStorage.getItem(TAB_KEY);
      if (HELP_TABS.some(t => t.key === saved)) return saved as HelpTabKey;
    } catch { /* no storage */ }
    return 'selling';
  });
  const setTab = (k: HelpTabKey) => {
    setTabState(k);
    try { localStorage.setItem(TAB_KEY, k); } catch { /* private window */ }
  };
  const activeTab = tabs.some(t => t.key === tab) ? tab : tabs[0]?.key;
  // On a phone the tab row swipes sideways: keep the picked tab in view.
  useEffect(() => {
    document.querySelector('.help-tabs .page-tab-btn.active')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const shown = q
    ? topics.filter(t => topicText(t).includes(q))
    : topics.filter(t => t.tab === activeTab);

  // Open topics; the first one of a tab starts open. Searching opens every match.
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [closedFirst, setClosedFirst] = useState<Set<string>>(() => new Set());
  const isOpen = (t: HelpTopic, i: number) =>
    !!q || open.has(t.key) || (i === 0 && !closedFirst.has(t.key));
  const toggle = (t: HelpTopic, i: number) => {
    const now = isOpen(t, i);
    if (i === 0) setClosedFirst(prev => { const n = new Set(prev); if (now) n.add(t.key); else n.delete(t.key); return n; });
    setOpen(prev => { const n = new Set(prev); if (now) n.delete(t.key); else n.add(t.key); return n; });
  };

  return (
    <AppLayout
      title="Help"
      toolbar={<HeaderSearch value={search} onChange={setSearch} placeholder="Search help…" label="Search help" />}
    >
      <div className="help-page">
        {!q && tabs.length > 0 && (
          <PageTabs
            className="help-tabs"
            tabs={tabs}
            active={activeTab ?? ''}
            onChange={k => setTab(k as HelpTabKey)}
          />
        )}
        {q && <div className="help-results">{shown.length} result{shown.length === 1 ? '' : 's'} for "{search.trim()}"</div>}

        {shown.length === 0 ? (
          <div className="help-empty">
            <IconEmptySearch />
            <span>{q ? 'Nothing found. Try another word.' : 'No help topics for your pages yet.'}</span>
          </div>
        ) : (
          <div className="help-list">
            {shown.map((t, i) => {
              const on = isOpen(t, i);
              return (
                <section key={t.key} className={`help-topic${on ? ' open' : ''}`}>
                  <button type="button" className="help-topic-head" onClick={() => toggle(t, i)} aria-expanded={on}>
                    <span className="help-topic-chev"><IconChevron /></span>
                    <span className="help-topic-titles">
                      <span className="help-topic-title">{t.title}</span>
                      <span className="help-topic-intro">{t.intro}</span>
                    </span>
                  </button>
                  {on && (
                    <div className="help-topic-body">
                      {t.sections.map(s => (
                        <div key={s.title} className="help-section">
                          <h4>{s.title}</h4>
                          {s.steps && <ol>{s.steps.map(x => <li key={x}>{x}</li>)}</ol>}
                          {s.points && <ul>{s.points.map(x => <li key={x}>{x}</li>)}</ul>}
                        </div>
                      ))}
                      <Link to={t.path} className="btn btn-secondary btn-sm help-open">Open {t.title}</Link>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
